import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Avatar,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Backdrop,
  CircularProgress,
  TextField
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Assignment as AssignmentIcon,
  TrendingUp as TrendingUpIcon,
  PersonOutline as PersonOutlineIcon,
  AccountBalanceWallet as CashIcon,
  AccountBalance as ExpenseIcon,
  PhoneAndroid as MobileIcon,
  CreditCard as CreditIcon,
  Receipt as ReceiptIcon,
  FilterList as FilterIcon,
  BarChart as BarChartIcon
} from '@mui/icons-material';
import { userTransaction } from '../BaseURL';
import axios from 'axios';
import Swal from 'sweetalert2';
import { formatWithOutSom } from '../Message';

const Home = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState('');

  const getLocalDateString = (date = new Date()) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [startDate, setStartDate] = useState(() => {
    return getLocalDateString();
  });

  const [endDate, setEndDate] = useState(() => {
    return getLocalDateString();
  });

  const [billingActivity, setBillingActivity] = useState([]);
  const [pettyExpenses, setPettyExpenses] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [summary, setSummary] = useState({ totalRevenue:0, totalReversal:0, totalExpenses:0, netRevenue:0 });

  const [statCards, setStatCards] = useState([
    {
      title: 'Cash Transactions',
      value: '0',
      subtitle: 'Total cash payments',
      icon: 'CashIcon',
      iconBg: '#e8f5e8',
      iconColor: '#2e7d32'
    },
    {
      title: 'Mobile Money',
      value: '0',
      subtitle: 'Mobile payment transactions',
      icon: 'MobileIcon',
      iconBg: '#e3f2fd',
      iconColor: '#1976d2'
    },
    {
      title: 'Mobile Money(manual)',
      value: '0',
      subtitle: 'Mobile payment transactions',
      icon: 'MobileIcon',
      iconBg: '#e3f2fd',
      iconColor: '#d21957ff'
    },
    {
      title: 'Reversed Transaction',
      value: '0',
      subtitle: 'Transaction Amount Canceled',
      icon: 'ScheduleIcon',
      iconBg: '#fff3e0',
      iconColor: '#00f5f5ff'
    },
    {
      title: 'Credit Transactions',
      value: '0',
      subtitle: 'Credit',
      icon: 'CreditIcon',
      iconBg: '#fff3e0',
      iconColor: '#f57c00'
    },
    {
      title: 'Petty Expenses',
      value: '0',
      subtitle: 'User petty expenses',
      icon: 'ExpenseIcon',
      iconBg: '#f3e5f5',
      iconColor: '#7b1fa2'
    },
    {
      title: 'Total Revenue',
      value: '0',
      subtitle: 'Total (Cash/Mobile Money)',
      icon: 'TrendingUpIcon',
      iconBg: '#fce4ec',
      iconColor: '#c2185b'
    }
  ]);

  useEffect(() => {
    const savedUserInfo = localStorage.getItem('userInfo');
    if (savedUserInfo) {
      const userInfo = JSON.parse(savedUserInfo);
      const userName = userInfo.name || '';
      console.log('Setting current user:', userName);
      setCurrentUser(userName);
    } else {
      console.log('No user info found in localStorage');
    }
  }, []);

  const loadBillingData = async (user = currentUser) => {
    try {
      console.log('Loading billing data for:', user);
      console.log('Date range:', startDate, 'to', endDate);

      if (!user) {
        console.log('No user provided, skipping loadBillingData');
        return;
      }

      setLoading(true);

      const formattedStartDate = startDate || getLocalDateString();
      const formattedEndDate = endDate || getLocalDateString();

      console.log('Formatted dates being sent:', formattedStartDate, 'to', formattedEndDate);

      const response = await axios.post(userTransaction, {
        currentUser: user,
        dat1: formattedStartDate,
        dat2: formattedEndDate
      });

      console.log('loadBillingData response:', response.data);

      if (response.data.success && response.data.results) {
        setBillingActivity(response.data.results);
        setPettyExpenses(response.data.pettyExpenses || []);
        console.log('Billing activity data loaded:', response.data.results.length, 'items');
        processTransactionData(response.data.results, response.data.pettyExpenses || []);
      } else {
        console.log('loadBillingData failed:', response.data.message);
        setBillingActivity([]);
        setPettyExpenses([]);
        setChartData([]);
      }
    } catch (error) {
      console.error('Error loading billing data:', error);
      setBillingActivity([]);
      setPettyExpenses([]);
      setChartData([]);
      await Swal.fire({
        title: 'Error',
        text: error.response?.data?.message || 'Failed to Load Billing Data',
        icon: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const processTransactionData = (data, expenses = []) => {
    if (!data || !Array.isArray(data)) {
      console.log('No valid data to process');
      return;
    }

    console.log('Processing transaction data:', data.length, 'items');

    const paymentTotals = data.reduce((acc, item) => {
      const paymentMode = item.paymentMode || 'Unknown';
      const amount   = parseFloat(item.amount)   || 0;
      const refund   = parseFloat(item.refund)   || 0;
      const reversal = parseFloat(item.reversal) || 0;
      const isReversal =
        String(item.incomeType || '').toLowerCase() === 'reversal' ||
        paymentMode === 'Reversed Transaction';

      // Reversal rows are inserted by /approveReversal with amount=0 and the
      // real reversed value in `reversal` (and a mirrored `refund`). Using
      // `amount` here would sum to 0 — which is exactly what produced the
      // "Reversed Transaction: 0.00" bug and prevented netRevenue from
      // subtracting the reversal. Use the reversal column instead, falling
      // back to refund for legacy rows that only have one of the two.
      const netAmount = isReversal
        ? (reversal || refund)
        : amount - refund;

      if (!acc[paymentMode]) {
        acc[paymentMode] = 0;
      }
      acc[paymentMode] += netAmount;
      return acc;
    }, {});

    console.log('Payment totals calculated:', paymentTotals);

    const chartDataArray = Object.entries(paymentTotals)
      .filter(([mode, amount]) => amount > 0)
      .map(([mode, amount]) => ({
        name: mode,
        value: amount,
        color: getPaymentModeColor(mode)
      }))
      .sort((a, b) => b.value - a.value);

    setChartData(chartDataArray);
    console.log('Chart data set:', chartDataArray);

    const cashTotal = paymentTotals['Cash'] || 0;
    const mobileTotal = paymentTotals['Mobile Money'] || 0;
    const MobileMoneyManualTotal = paymentTotals['Mobile Money(manual)'] || 0;
    const ReversedTransactionTotal = paymentTotals['Reversed Transaction'] || 0;
    const creditTotal = paymentTotals['Credit'] || 0;
    const pettyExpensesTotal = expenses.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    const totalRevenue = cashTotal + mobileTotal + MobileMoneyManualTotal;
    const totalReversal = paymentTotals['Reversed Transaction'] || 0;
    const totalExpenses = expenses.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    const netRevenue = totalRevenue - totalReversal - totalExpenses;

    setSummary({ totalRevenue, totalReversal, totalExpenses, netRevenue });

    console.log('Updating stat cards with:', {
      cashTotal,
      mobileTotal,
      MobileMoneyManualTotal,
      ReversedTransactionTotal,
      creditTotal,
      pettyExpensesTotal,
      totalRevenue
    });

    setStatCards(prevCards =>
      prevCards.map(card => {
        switch (card.title) {
          case 'Cash Transactions':
            return { ...card, value: cashTotal.toFixed(2) };
          case 'Mobile Money':
            return { ...card, value: mobileTotal.toFixed(2) };
          case 'Mobile Money(manual)':
            return { ...card, value: MobileMoneyManualTotal.toFixed(2) };
          case 'Reversed Transaction':
            return { ...card, value: ReversedTransactionTotal.toFixed(2) };
          case 'Credit Transactions':
            return { ...card, value: creditTotal.toFixed(2) };
          case 'Petty Expenses':
            return { ...card, value: pettyExpensesTotal.toFixed(2) };
          case 'Total Revenue':
            return { ...card, value: totalRevenue.toFixed(2) };
          default:
            return card;
        }
      })
    );
  };

  const getPaymentModeColor = paymentMode => {
    const colors = {
      Cash: '#4caf50',
      'Mobile Money': '#2196f3',
      'Mobile Money(manual)': '#f32133ff',
      'Reversed Transaction': '#00f5f5ff',
      Credit: '#ff9800'
    };
    return colors[paymentMode] || '#757575';
  };

  const iconMap = {
    ScheduleIcon: ScheduleIcon,
    CheckCircle: CheckCircleIcon,
    WarningIcon: WarningIcon,
    AssignmentIcon: AssignmentIcon,
    CashIcon: CashIcon,
    MobileIcon: MobileIcon,
    CreditIcon: CreditIcon,
    ExpenseIcon: ExpenseIcon,
    TrendingUpIcon: TrendingUpIcon
  };

  useEffect(() => {
    if (currentUser) {
      console.log('Current user changed to:', currentUser);
      loadBillingData(currentUser);
    }
  }, [currentUser, startDate, endDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const getFilteredChartData = useMemo(() => {
    console.log('Available chart data:', chartData);

    if (chartData.length === 0) {
      console.log('No chart data available');
      return [];
    }

    return chartData.slice(0, 10);
  }, [chartData]);

  const getFilteredActivityData = useMemo(() => {
    console.log('Available billing activity:', billingActivity.length, 'items');

    if (billingActivity.length === 0) {
      return [];
    }

    const sortedData = [...billingActivity].sort((a, b) => new Date(b.date) - new Date(a.date));

    console.log('Sorted billing activity data:', sortedData.length, 'items');
    return sortedData;
  }, [billingActivity]);

  const topTransactions = getFilteredChartData;

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDate = dateString => {
    let date;
    if (typeof dateString === 'string' && dateString.includes('T')) {
      date = new Date(dateString);
    } else {
      const parts = dateString.split('-');
      date = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
    }

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC'
    });
  };

  const getPaymentModeChip = paymentMode => {
    const modeColors = {
      Cash: { bg: '#e8f5e8', color: '#2e7d32' },
      'Mobile Money': { bg: '#e3f2fd', color: '#1976d2' },
      'Mobile Money(manual)': { bg: '#e3f2fd', color: '#d21960ff' },
      'Reversed Transaction': { bg: '#e3f2fd', color: '#00f5f5ff' },
      Credit: { bg: '#fff3e0', color: '#f57c00' }
    };

    const modeColor = modeColors[paymentMode] || { bg: '#f5f5f5', color: '#424242' };

    return (
      <Chip
        label={paymentMode}
        size="small"
        sx={{
          backgroundColor: modeColor.bg,
          color: modeColor.color,
          fontWeight: 600,
          fontSize: '0.75rem'
        }}
      />
    );
  };

  const getDateRangeText = () => {
    if (startDate === endDate) {
      return formatDate(startDate);
    }
    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
  };

  const handleDateRangeFilter = () => {
    setPage(0);
    console.log('Applying date range filter:', startDate, 'to', endDate);
    if (currentUser) {
      loadBillingData(currentUser);
    }
  };

  const setToYesterday = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayString = getLocalDateString(yesterday);
    setStartDate(yesterdayString);
    setEndDate(yesterdayString);
  };

  const PaymentModeChart = () => {
    const maxChartValue = Math.max(...topTransactions.map(item => item.value), 1);

    return (
      <Card elevation={2} sx={{ height: '100%', borderRadius: 2 }}>
        <CardContent sx={{ p: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Avatar
              sx={{
                backgroundColor: '#f3e5f5',
                color: '#7b1fa2',
                width: 48,
                height: 48,
                mr: 2
              }}
            >
              <BarChartIcon />
            </Avatar>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h6" fontWeight="600" color="text.primary">
                Payment Methods
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Revenue breakdown
              </Typography>
            </Box>
          </Box>

          {topTransactions.length > 0 ? (
            <Box sx={{ height: 200, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {topTransactions.slice(0, 5).map((item, index) => (
                <Box key={index}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                      {item.name}
                    </Typography>
                    <Typography variant="body2" fontWeight="600" sx={{ fontSize: '0.75rem' }}>
                      {formatWithOutSom(item.value)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: '100%',
                      height: 8,
                      backgroundColor: '#f5f5f5',
                      borderRadius: 1,
                      overflow: 'hidden'
                    }}
                  >
                    <Box
                      sx={{
                        width: `${(item.value / maxChartValue) * 100}%`,
                        height: '100%',
                        backgroundColor: item.color,
                        borderRadius: 1,
                        transition: 'width 0.5s ease-in-out'
                      }}
                    />
                  </Box>
                </Box>
              ))}
            </Box>
          ) : (
            <Box
              sx={{
                height: 200,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'text.secondary'
              }}
            >
              <Typography variant="body2">No payment data available</Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: theme => theme.zIndex.drawer + 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 2
        }}
        open={loading}
      >
        <CircularProgress color="inherit" size={60} />
        <Typography variant="h6" color="inherit">
          Loading Dashboard...
        </Typography>
      </Backdrop>

      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 2
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1e293b', mb: 1 }}>
              Billing & Accounts Dashboard
            </Typography>
            <Typography variant="subtitle1" sx={{ color: '#64748b' }}>
              Welcome {currentUser}. Here's your billing overview for {getDateRangeText()}.
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 1 }}>
              Debug: Start Date: {startDate}, End Date: {endDate}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FilterIcon sx={{ color: '#64748b' }} />
            <TextField
              label="Start Date"
              type="date"
              size="small"
              value={startDate}
              onChange={e => {
                console.log('Start date changed to:', e.target.value);
                setStartDate(e.target.value);
              }}
              InputLabelProps={{
                shrink: true
              }}
              sx={{ minWidth: 150 }}
            />
            <TextField
              label="End Date"
              type="date"
              size="small"
              value={endDate}
              onChange={e => {
                console.log('End date changed to:', e.target.value);
                setEndDate(e.target.value);
              }}
              InputLabelProps={{
                shrink: true
              }}
              sx={{ minWidth: 150 }}
            />
            <Button
              variant="contained"
              onClick={handleDateRangeFilter}
              sx={{
                backgroundColor: '#1976d2',
                '&:hover': {
                  backgroundColor: '#1565c0'
                }
              }}
            >
              Apply Filter
            </Button>
            <Button variant="outlined" onClick={setToYesterday} size="small">
              Yesterday
            </Button>
          </Box>
        </Box>
      </Paper>


     

      <Grid container spacing={3} justifyContent="center" alignItems="center" sx={{ mb: 1 }}>
        {statCards.map((card, index) => {
          const IconComponent = iconMap[card.icon] || ScheduleIcon;

          return (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card
                elevation={2}
                sx={{
                  borderRadius: 2,
                  textAlign: 'center',
                  height: '100%'
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Avatar
                    sx={{
                      backgroundColor: card.iconBg,
                      color: card.iconColor,
                      width: 48,
                      height: 48,
                      mx: 'auto',
                      mb: 1
                    }}
                  >
                    <IconComponent />
                  </Avatar>
                  <Typography variant="h5" fontWeight="700">
                    {formatWithOutSom(card.value)}
                  </Typography>
                  <Typography variant="subtitle2" color="text.secondary" fontWeight="600">
                    {card.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {card.subtitle}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          );
        })}

        <Grid item xs={12} sm={12} md={4}>
  <Card
    elevation={3}
    sx={{
      borderRadius: 3,
      background: 'linear-gradient(135deg, #4caf50, #66bb6a)',
      color: '#fff',
      height: '100%',
    }}
  >
    <CardContent sx={{ textAlign: 'center', py: 2 }}>
      <Typography variant="subtitle1" fontWeight="700">
        Accounting Summary
      </Typography>

      <Typography variant="caption">Gross</Typography>
      <Typography variant="h6" fontWeight="700">
        {formatWithOutSom(summary.totalRevenue)}
      </Typography>

      <Typography variant="caption">Reversal</Typography>
      <Typography variant="body2">
        -{formatWithOutSom(summary.totalReversal)}
      </Typography>

      <Typography variant="caption">Expenses</Typography>
      <Typography variant="body2">
        -{formatWithOutSom(summary.totalExpenses)}
      </Typography>

      <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.3)', my: 1 }} />

      <Typography variant="caption">Net</Typography>
      <Typography variant="h6" fontWeight="800">
        {formatWithOutSom(summary.netRevenue)}
      </Typography>
    </CardContent>
  </Card>
</Grid>

        <Grid item xs={12} sm={12} md={4}>
          <PaymentModeChart />
        </Grid>
      </Grid>

      <Card elevation={2} sx={{ mt: 3, borderRadius: 2 }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <ReceiptIcon sx={{ mr: 1.5, color: 'text.secondary' }} />
              <Box>
                <Typography variant="h6" fontWeight="600">
                  Recent Billing Activity
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {getDateRangeText()} | Showing {getFilteredActivityData.length} records
                </Typography>
              </Box>
            </Box>
          </Box>

          {getFilteredActivityData.length > 0 ? (
            <>
              <TableContainer>
                <Table sx={{ minWidth: 750 }} aria-label="billing activity table">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                      <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Receipt ID</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Narration</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Income Type</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: '#374151' }}>
                        Amount
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: '#374151' }}>
                        Reversal
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Payment Mode</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Patient Name</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {getFilteredActivityData
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((activity, index) => (
                        <TableRow
                          key={activity.id || index}
                          sx={{
                            '&:hover': { backgroundColor: '#f8f9fa' },
                            '&:last-child td, &:last-child th': { border: 0 }
                          }}
                        >
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {formatDate(activity.date)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="500" color="primary">
                              {activity.receiptId || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{activity.narration || 'N/A'}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {activity.incomeType || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="600" color="success.main">
                              {formatWithOutSom(activity.amount)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            {(() => {
                              // /approveReversal writes the reversed value to BOTH
                              // refund and reversal columns. Prefer whichever is
                              // populated so the column shows the right amount even
                              // for legacy rows that only have one.
                              const reversedAmt = parseFloat(activity.refund) || parseFloat(activity.reversal) || 0;
                              return reversedAmt > 0 ? (
                                <Typography variant="body2" fontWeight="600" color="error.main">
                                  -{formatWithOutSom(reversedAmt)}
                                </Typography>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  -
                                </Typography>
                              );
                            })()}
                          </TableCell>
                          <TableCell>{getPaymentModeChip(activity.paymentMode || 'Unknown')}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Avatar
                                sx={{
                                  width: 28,
                                  height: 28,
                                  mr: 1,
                                  backgroundColor: '#e3f2fd',
                                  fontSize: '0.75rem'
                                }}
                              >
                                <PersonOutlineIcon sx={{ fontSize: 16, color: '#1976d2' }} />
                              </Avatar>
                              <Typography variant="body2">{activity.patientName || 'Unknown'}</Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={getFilteredActivityData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{
                  borderTop: '1px solid #e0e0e0',
                  mt: 2,
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                    fontSize: '0.875rem',
                    color: '#64748b'
                  },
                  '& .MuiTablePagination-select': {
                    fontSize: '0.875rem'
                  },
                  '& .MuiIconButton-root': {
                    color: '#64748b'
                  }
                }}
              />
            </>
          ) : (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: 200,
                color: 'text.secondary'
              }}
            >
              <Typography variant="body1">No billing activity found for the selected date range</Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default Home;
